import * as Tooltip from '@radix-ui/react-tooltip';
import type { OrganizationInvitation, OrganizationMember } from '@reach/shared-types/workos-auth';
import { ChevronLeft, ChevronRight, Loader2, UserCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { MemberActions } from './MemberActions';

interface MembersTableProps {
  members: OrganizationMember[];
  invitations: OrganizationInvitation[];
  isLoadingMembers: boolean;
  isLoadingInvitations: boolean;
  isFetchingMembers: boolean;
  isRevokingInvitation: boolean;
  currentUserId: string | undefined;
  pagination?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    cursor: string | null;
    previousCursor: string | null;
  };
  onNextPage: () => void;
  onPreviousPage: () => void;
  onDeactivate: (member: OrganizationMember) => void;
  onReactivate: (member: OrganizationMember) => void;
  onRevokeInvitation: (invitationId: string) => void;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function MembersTable({
  members,
  invitations,
  isLoadingMembers,
  isLoadingInvitations,
  isFetchingMembers,
  isRevokingInvitation,
  currentUserId,
  pagination,
  onNextPage,
  onPreviousPage,
  onDeactivate,
  onReactivate,
  onRevokeInvitation,
}: MembersTableProps) {
  const pendingInvitations = invitations.filter(invitation => invitation.state === 'pending');
  const isLoading = isLoadingMembers || isLoadingInvitations;
  const hasRows = members.length > 0 || pendingInvitations.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-base font-medium text-gray-900">People</h2>
      </div>
      {isLoading ? (
        <div className="px-6 py-12 flex items-center justify-center text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading people...
        </div>
      ) : !hasRows ? (
        <div className="px-6 py-12 text-center text-gray-500 text-sm">
          No members or invitations found.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Person
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {members.map(member => {
                  const isCurrentUser = member.userId === currentUserId;
                  const isSupportAccount = member.user.email?.endsWith('@embedreach.com');
                  const displayName = [member.user.firstName, member.user.lastName]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {member.user.profilePictureUrl ? (
                            <img
                              src={member.user.profilePictureUrl}
                              alt=""
                              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <UserCircle className="h-8 w-8 text-gray-300 flex-shrink-0" />
                          )}
                          <div className="ml-3 flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {displayName || 'Unknown'}
                            </span>
                            {isCurrentUser && <span className="text-xs text-gray-500">(you)</span>}
                            {isSupportAccount && (
                              <Tooltip.Provider delayDuration={200}>
                                <Tooltip.Root>
                                  <Tooltip.Trigger asChild>
                                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20 cursor-help">
                                      Support
                                    </span>
                                  </Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content
                                      side="top"
                                      sideOffset={4}
                                      className="z-50 max-w-xs px-3 py-2 text-xs text-white bg-gray-900 rounded-md shadow-md">
                                      Reach employees may have access to your dashboard to provide better
                                      support.
                                      <Tooltip.Arrow className="fill-gray-900" />
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              </Tooltip.Provider>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {member.user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20 capitalize">
                          {member.role.slug}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${
                            member.status === 'active'
                              ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                              : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20'
                          }`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <MemberActions
                          member={member}
                          isCurrentUser={isCurrentUser}
                          onDeactivate={() => onDeactivate(member)}
                          onReactivate={() => onReactivate(member)}
                        />
                      </td>
                    </tr>
                  );
                })}

                {pendingInvitations.map(invitation => (
                  <tr key={invitation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserCircle className="h-8 w-8 text-gray-300 flex-shrink-0" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">Invitation pending</p>
                          <p className="text-xs text-gray-500">
                            Sent {formatDate(invitation.createdAt)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {invitation.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20 capitalize">
                        admin
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                        pending
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRevokeInvitation(invitation.id)}
                        disabled={isRevokingInvitation}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(pagination?.hasNextPage || pagination?.hasPreviousPage) && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={onPreviousPage}
                disabled={!pagination?.hasPreviousPage || isFetchingMembers}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onNextPage}
                disabled={!pagination?.hasNextPage || isFetchingMembers}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
